import {
  ALERT_MESSAGE_REQUIRED_PARAMS,
  DEVICE_ALERT_SEVERITY_ORDER,
  resolveAlertSeverity,
} from '../constants/device-alerts';
import type { useIntl } from '../hooks/useIntl';
import { messages } from '../i18n';
import type { DeviceAlert, DeviceAlertSeverity } from '../types/device-alert';
import { hzToMhz } from './frequency';
import { formatOptionalNumber } from './number-format';

type Intl = ReturnType<typeof useIntl>;

const PERCENT_SCALE = 100;

const formatAlertParam = (intl: Intl, name: string, value: unknown): string => {
  if (name === 'frequency') {
    return intl.formatMessage(
      { id: 'alerts.params.frequencyMhz' },
      { value: formatOptionalNumber(hzToMhz(Number(value))) },
    );
  }

  if (name === 'similarity') {
    return intl.formatMessage(
      { id: 'alerts.params.similarityPercent' },
      { value: Math.round(Number(value) * PERCENT_SCALE) },
    );
  }

  return String(value);
};

// `hzToMhz` maps a non-numeric value to 0, so a malformed number must be caught here, before it
// can render as a plausible reading.
const NUMERIC_ALERT_PARAMS = new Set(['frequency']);

const isRenderableParam = (name: string, value: unknown): boolean =>
  value != null && (!NUMERIC_ALERT_PARAMS.has(name) || Number.isFinite(Number(value)));

const describeKnownAlert = (intl: Intl, alert: DeviceAlert): string | null => {
  const messageId = `alerts.${alert.source}.${alert.type}`;
  const requiredParams = ALERT_MESSAGE_REQUIRED_PARAMS[`${alert.source}.${alert.type}`];

  if (!requiredParams || messages[intl.locale][messageId] === undefined) {
    return null;
  }

  if (requiredParams.some((name) => !isRenderableParam(name, alert.params[name]))) {
    return null;
  }

  const values = Object.fromEntries(
    Object.entries(alert.params).map(([name, value]) => [
      name,
      formatAlertParam(intl, name, value),
    ]),
  );

  return intl.formatMessage({ id: messageId }, values);
};

export const describeDeviceAlert = (intl: Intl, alert: DeviceAlert): string => {
  const fallback = () =>
    intl.formatMessage(
      { id: 'alerts.fallback' },
      { source: String(alert.source), type: String(alert.type) },
    );

  // params arrive from the wire untyped (zahrada degrades an oversized event to `{}`), so a value
  // that cannot even be stringified must still leave the card readable.
  try {
    return describeKnownAlert(intl, alert) ?? fallback();
  } catch {
    return fallback();
  }
};

const severityRank = (severity: string): number =>
  DEVICE_ALERT_SEVERITY_ORDER.indexOf(resolveAlertSeverity(severity));

export const sortDeviceAlertsBySeverity = (alerts: DeviceAlert[]): DeviceAlert[] =>
  [...alerts].sort(
    (first, second) =>
      severityRank(first.severity) - severityRank(second.severity) ||
      Date.parse(second.raisedAt) - Date.parse(first.raisedAt),
  );

export const highestDeviceAlertSeverity = (alerts: DeviceAlert[]): DeviceAlertSeverity =>
  resolveAlertSeverity(sortDeviceAlertsBySeverity(alerts)[0]?.severity ?? 'info');
